import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { LuLoader2 } from "react-icons/lu";
import { ConfirmDialogProvider } from "./contexts/ConfirmDialogContext";
import { DossierPersonneProvider } from "./contexts/DossierPersonneContext";
import { useFenetresDialogueDeplacables } from "./hooks/useFenetresDialogueDeplacables";
import MainLayout from "./layout/MainLayout";
import RouteAccueil from "./components/RouteAccueil";
import Login from "./pages/Login";
import PrivateRoute from "./components/PrivateRoute";
import SplashConnexion from "./components/accueil/SplashConnexion";
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';

// Chargées à la demande (React.lazy) plutôt qu'au démarrage de l'app : ces
// pages ne sont visitées qu'après navigation (jamais au premier écran), et
// certaines embarquent des dépendances lourdes (EspaceDossier → pdf-lib +
// ScannerDocument → tesseract.js) qui gonflaient le bundle principal — voir
// l'avertissement "bundle size is significantly larger than recommended" du
// build. Login/RouteAccueil restent chargées immédiatement : ce sont les deux
// tout premiers écrans possibles, pas la peine d'ajouter un flash de
// chargement dessus.
const EspaceDossier = lazy(() => import("./pages/EspaceDossier"));
const Personnel = lazy(() => import("./pages/Personnel"));
const Pai = lazy(() => import("./pages/Pai"));
const PaiDetail = lazy(() => import("./pages/PaiDetail"));
const ProtocolePai = lazy(() => import("./pages/ProtocolePai"));
const Document = lazy(() => import("./pages/Document"));
const Settings = lazy(() => import("./pages/Settings"));
const OpenFolder = lazy(() => import("./pages/OpenFolder"));
const Profile = lazy(() => import("./pages/Profile"));
const DocView = lazy(() => import("./pages/DocView"));
const EditionWord = lazy(() => import("./pages/EditionWord"));
const Formation = lazy(() => import("./pages/Formation"));
const Corbeille = lazy(() => import("./pages/Corbeille"));
const Activite = lazy(() => import("./pages/Activite"));
// recharts est une dépendance lourde (comme pdf-lib/tesseract.js ci-dessus) —
// chargée uniquement à la visite de cette page.
const Statistiques = lazy(() => import("./pages/Statistiques"));
const Contact = lazy(() => import("./pages/Contact"));
const Courriers = lazy(() => import("./pages/Courriers"));
const AppelsTelephoniques = lazy(() => import("./pages/AppelsTelephoniques"));
const Cheques = lazy(() => import("./pages/Cheques"));
const FluctuationRh = lazy(() => import("./pages/FluctuationRh"));
const PartageExterne = lazy(() => import("./pages/PartageExterne"));
const Inscription = lazy(() => import("./pages/Inscription"));
const MotDePasseOublie = lazy(() => import("./pages/MotDePasseOublie"));
const NotFound = lazy(() => import("./pages/NotFound"));

function ChargementPage() {
  return (
    <div className="flex w-full items-center justify-center py-24">
      <LuLoader2 className="animate-spin text-muted-foreground" size={24} />
    </div>
  );
}

function App() {
  useFenetresDialogueDeplacables();

  return (
    <ConfirmDialogProvider>
      <BrowserRouter>
        {/* À l'intérieur de BrowserRouter (pas à côté de ConfirmDialogProvider,
            plus haut) : le volet a besoin de useNavigate() pour emmener vers
            l'élément cliqué de sa chronologie (voir DossierPersonneContext.jsx). */}
        <DossierPersonneProvider>
        {/* Avant les routes : il doit recouvrir le premier chargement de
            /auth/me et lire son drapeau avant que RouteAccueil ne décide
            d'afficher le tour de bienvenue (voir utils/splash.js). */}
        <SplashConnexion />
        <Suspense fallback={<ChargementPage />}>
          <Routes>
            <Route path="/" element={<MainLayout />}>
              <Route element={<PrivateRoute />}>
                <Route index element={<RouteAccueil />} />
                <Route path="espace/:type" element={<EspaceDossier />} />
                <Route path="personnel" element={<Personnel />} />
                <Route path="pai" element={<Pai />} />
                <Route path="pai/:id" element={<PaiDetail />} />
                <Route path="pai/:id/protocole" element={<ProtocolePai />} />
                <Route path="doc" element={<Document />} />
                <Route path="setting" element={<Settings />} />
                <Route path="profile" element={<Profile />} />
                <Route path="view/:id/:type" element={<DocView />} />
                <Route path="folder/:id" element={<OpenFolder/>} />
                <Route path="corbeille" element={<Corbeille/>} />
                <Route path="activite" element={<Activite/>} />
                <Route path="statistiques" element={<Statistiques/>} />
                <Route path="courriers" element={<Courriers/>} />
                <Route path="appels" element={<AppelsTelephoniques/>} />
                <Route path="cheques" element={<Cheques/>} />
                <Route path="fluctuation-rh" element={<FluctuationRh/>} />
                <Route path="formation" element={<Formation/>} />
                <Route path="contact" element={<Contact/>} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
            {/* Hors de MainLayout (pas de Sidebar/Navbar) : l'éditeur Word a besoin
                de tout l'écran, pas juste l'espace restant sous la barre du haut —
                voir EditionWord.jsx (h-screen). Toujours protégé par PrivateRoute. */}
            <Route element={<PrivateRoute />}>
              <Route path="/view/:id/editer-word" element={<EditionWord />} />
              <Route path="/view/:id/editer-word/version/:versionId" element={<EditionWord />} />
            </Route>
            <Route path="/login" element={<Login />} />
            <Route path="/inscription" element={<Inscription />} />
            <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
            <Route path="/partage/:token" element={<PartageExterne />} />
          </Routes>
        </Suspense>
        <ToastContainer />
        </DossierPersonneProvider>
      </BrowserRouter>
    </ConfirmDialogProvider>
  );
}

export default App;
